"use client";

import { useState, useEffect } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { doc, getDoc, collection, addDoc, serverTimestamp } from "firebase/firestore";
import { db } from "../../firebase";
import { Heart, Wallet, Gift, ShieldOff } from "lucide-react";
import type { UniversalLinkConfig } from "./UniversalLinkPreview";

interface UserData {
  wallet: string;
  smartWallet: string;
  username: string;
  photoURL?: string;
  isBusiness?: boolean;
  collectSettings?: {
    requestPaymentEnabled?: boolean;
    buyMeCoffeeEnabled?: boolean;
    donationBoxEnabled?: boolean;
    paymentTheme?: "dark" | "light";
  };
  universalLinkConfig?: UniversalLinkConfig;
}

export default function UniversalProfilePage() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [userData, setUserData] = useState<UserData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchData = async () => {
      if (!slug) {
        setError("Invalid link.");
        setLoading(false);
        return;
      }

      try {
        // Resolve slug to wallet
        const slugDoc = await getDoc(doc(db, "slugs", slug.toLowerCase()));
        if (!slugDoc.exists()) {
          setError("This payment link does not exist.");
          setLoading(false);
          return;
        }

        const walletAddress = slugDoc.data().walletAddress?.toLowerCase() || "";

        // Fetch user data
        const userDoc = await getDoc(doc(db, "users", walletAddress));
        if (!userDoc.exists()) {
          setError("User not found.");
          setLoading(false);
          return;
        }

        const data = userDoc.data() as UserData;
        setUserData(data);

        // If mode is "direct" or no config, redirect to payment page
        const config = data.universalLinkConfig;
        if (!config || config.mode === "direct") {
          navigate(`/pay/${slug}/payment`, { replace: true });
          return;
        }

        // Log page view
        addDoc(collection(db, "users", walletAddress, "pageViews"), {
          type: "universal",
          timestamp: serverTimestamp(),
        }).catch(() => {});
      } catch (err) {
        console.error("Error loading profile:", err);
        setError("Failed to load page. Please try again.");
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [slug, navigate]);

  if (loading) {
    return <div className="min-h-screen bg-[#090D14]" />;
  }

  if (error || !userData) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-[#090D14] px-4">
        <ShieldOff className="w-16 h-16 text-gray-600 mb-4" />
        <h1 className="text-xl font-bold text-white mb-2">Page Not Found</h1>
        <p className="text-gray-400 text-center mb-6">{error || "Something went wrong."}</p>
        <Link to="/" className="text-[#61CE70] hover:underline text-sm">
          Go to CashTap
        </Link>
      </div>
    );
  }

  const config = userData.universalLinkConfig!;
  const settings = userData.collectSettings || {};
  const isLight = settings.paymentTheme === "light";
  const color = config.primaryColor || "#61CE70";
  const title = config.title || userData.username || "User";
  const bio = config.bio || "Welcome to my page";

  const links = [
    {
      key: "payment",
      label: config.paymentLabel || "Send Payment",
      icon: <Wallet size={22} />,
      show: config.showPayment && settings.requestPaymentEnabled !== false,
      gradient: "from-emerald-500 to-teal-600",
      href: `/pay/${slug}/payment`,
    },
    {
      key: "donation",
      label: config.donationLabel || "Donation Box",
      icon: <Heart size={22} />,
      show: config.showDonation && settings.donationBoxEnabled !== false,
      gradient: "from-pink-500 to-rose-600",
      href: `/pay/${slug}/donate`,
    },
    {
      key: "coffee",
      label: config.coffeeLabel || "Support Tips Jar",
      icon: <Gift size={22} />,
      show: config.showCoffee && settings.buyMeCoffeeEnabled !== false,
      gradient: "from-amber-400 to-orange-500",
      href: `/pay/${slug}/coffee`,
    },
  ].filter((l) => l.show);

  return (
    <div
      className={`min-h-screen ${isLight ? "bg-gray-50" : "bg-[#090D14]"}`}
    >
      {/* Header */}
      <header
        className={`w-full border-b ${isLight ? "border-gray-200 bg-white/80" : "border-gray-800/50 bg-[#090D14]/80"} backdrop-blur-lg`}
      >
        <div className="max-w-5xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-center">
          <Link
            to="/"
            className={`flex items-center gap-2.5 text-base ${isLight ? "text-gray-500" : "text-gray-400"}`}
          >
            <span className="font-medium">Powered by</span>
            <img src="/logo.png" alt="CashTap" className="h-12 scale-125 ml-3 w-auto" />
          </Link>
        </div>
      </header>

      {/* Main */}
      <main className="max-w-lg mx-auto px-4 sm:px-6 pb-12">
        {/* Cover */}
        <div className="relative -mx-4 sm:-mx-6 mb-14">
          <div className="overflow-hidden">
            {config.backgroundImage ? (
              <div className="relative">
                <img
                  src={config.backgroundImage}
                  alt="Cover"
                  className="w-full h-48 sm:h-64 object-cover"
                />
                <div
                  className={`absolute inset-0 ${isLight ? "bg-gradient-to-t from-gray-50 via-gray-50/40 to-transparent" : "bg-gradient-to-t from-[#090D14] via-[#090D14]/40 to-transparent"}`}
                />
              </div>
            ) : (
              <div
                className="w-full h-48 sm:h-64"
                style={{
                  background: isLight
                    ? `linear-gradient(135deg, ${color}20, ${color}08, #f9fafb)`
                    : `linear-gradient(135deg, ${color}30, ${color}10, #090D14)`,
                }}
              />
            )}
          </div>

          {/* Avatar */}
          <div className="absolute -bottom-12 left-1/2 -translate-x-1/2 z-10">
            <div
              className={`w-24 h-24 rounded-full overflow-hidden border-4 shadow-xl ${isLight ? "border-gray-50 bg-white" : "border-[#090D14] bg-[#090D14]"}`}
            >
              {userData.photoURL ? (
                <img
                  src={userData.photoURL}
                  alt={userData.username}
                  className="w-full h-full object-cover"
                />
              ) : (
                <div
                  className="w-full h-full flex items-center justify-center"
                  style={{
                    background: `linear-gradient(135deg, ${color}33, ${color}11)`,
                  }}
                >
                  <span className="text-3xl font-bold text-white/80">
                    {(userData.username || "?")[0]?.toUpperCase()}
                  </span>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Name & Bio */}
        <div className="text-center mb-8">
          <h1
            className={`text-2xl sm:text-3xl font-bold mb-2 ${isLight ? "text-gray-900" : "text-white"}`}
          >
            {title}
            {userData.isBusiness && (
              <span className={`ml-2 px-3 py-1 text-xs font-semibold rounded-full inline-flex items-center gap-1.5 align-middle ${
                isLight ? "bg-blue-100 text-blue-700" : "bg-blue-500/20 text-blue-400"
              }`}>
                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                </svg>
                Business
              </span>
            )}
          </h1>
          <p
            className={`text-sm sm:text-base max-w-md mx-auto leading-relaxed ${isLight ? "text-gray-500" : "text-gray-400"}`}
          >
            {bio}
          </p>
        </div>

        {/* Links */}
        <div className="space-y-3">
          {links.map((link) => (
            <Link
              key={link.key}
              to={link.href}
              className={`group relative flex items-center gap-4 px-5 sm:px-6 py-4 sm:py-5 rounded-2xl border transition-all hover:scale-[1.02] active:scale-[0.98] ${
                isLight
                  ? "bg-white border-gray-200 shadow-sm hover:shadow-md hover:border-gray-300"
                  : "bg-gradient-to-r from-gray-800/40 to-gray-800/20 border-gray-700/40 hover:border-gray-600/60"
              }`}
            >
              <div
                className={`w-12 h-12 rounded-xl bg-gradient-to-br ${link.gradient} flex items-center justify-center text-white shadow-lg shrink-0`}
              >
                {link.icon}
              </div>
              <span
                className={`font-semibold text-base flex-1 ${isLight ? "text-gray-800" : "text-white"}`}
              >
                {link.label}
              </span>
              <svg
                className={`w-5 h-5 transition-transform group-hover:translate-x-1 ${isLight ? "text-gray-400" : "text-gray-500 group-hover:text-gray-300"}`}
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 5l7 7-7 7"
                />
              </svg>
            </Link>
          ))}

          {links.length === 0 && (
            <div
              className={`text-center py-12 rounded-2xl border ${isLight ? "border-gray-200 bg-white" : "border-gray-700/50 bg-gray-800/20"}`}
            >
              <p className={`${isLight ? "text-gray-400" : "text-gray-500"} text-sm`}>
                No payment options available at the moment.
              </p>
            </div>
          )}
        </div>
      </main>
    </div>
  );
}
